import EditIcon from "../../images/confirmation/edit.svg?react";
import styles from "../index.module.css";
import { Typography, Button, TypographyVariant, Tooltip, Link } from "@mui/material";
import i18nPackage from "../../../i18n/i18n";
import { EditQuizStep, getEditQuizStepName } from "../../proctoring-options/option-text/ProctoringOptionText";
import IncompleteIcon from "../../images/incomplete-icon.svg?react";
import {
	CoreIconButton,
	CoreInfoIndicator,
	CoreRadioCardIcon,
	CoreSkeletonWrapper,
	EllipsisTooltip,
	OverflowText,
} from "../../../core";
import { forwardRef, isValidElement, useState } from "react";
import { ConditionalWrapper } from "../../../wrapper";
import { useOverflow } from "../../../hooks";

type ViewMoreTextProps = {
	children: React.ReactNode;
	className?: string;
};

function ViewMoreText({ children, className }: ViewMoreTextProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [ref, isOverflowing] = useOverflow();

	return (
		<div className={styles.viewMoreTextContainer}>
			<Typography
				className={className}
				sx={{
					display: isExpanded ? "block" : "-webkit-box",
					WebkitLineClamp: isExpanded ? "unset" : 3,
					WebkitBoxOrient: "vertical",
					overflow: isExpanded ? "visible" : "hidden",
					wordBreak: "break-word",
					textOverflow: "ellipsis",
					width: "100%",
					maxWidth: "600px",
				}}
				ref={ref}
			>
				{children}
			</Typography>

			{(isOverflowing || isExpanded) && (
				<Link component="button" type="button" onClick={() => setIsExpanded(!isExpanded)} aria-expanded={isExpanded}>
					{i18nPackage.t(`editQuizPage.confirmationBlockTSX.view${isExpanded ? "Less" : "More"}`)}
				</Link>
			)}
		</div>
	);
}

/**Ellipsis = EllipsisTooltip,
 * Overflow = OverflowText,
 * Wrap = Text will wrap to next line,
 * viewMore = default 3 lines, then a view more which will show the rest of the text */
type TooltipType = "overflow" | "ellipsis" | "wrap" | "viewMore";

// TODO: refactor so consumers can pass in customized props depending on tooltipType
const renderWrapper = (children: React.ReactNode, className: string, tooltipType?: TooltipType, disabled?: boolean) => {
	switch (tooltipType) {
		case "overflow":
			return (
				<OverflowText className={className} disabled={disabled}>
					{children}
				</OverflowText>
			);
		case "ellipsis":
			if (isValidElement(children)) {
				return <EllipsisTooltip entry={children}>{children}</EllipsisTooltip>;
			}
			return (
				<EllipsisTooltip entry={children}>
					<span className={className}>{children}</span>
				</EllipsisTooltip>
			);
		case "wrap":
			return <Typography className={`${className} ${styles.wrap}`}>{children}</Typography>;
		case "viewMore":
			return <ViewMoreText className={className}>{children}</ViewMoreText>;
		default:
			return <Typography className={className}>{children}</Typography>;
	}
};

export function ConfirmationRowTitle({
	children,
	tooltipType,
}: {
	children: React.ReactNode;
	tooltipType?: TooltipType;
}) {
	return renderWrapper(children, styles.name, tooltipType);
}

export function ConfirmationRowDescription({
	children,
	tooltipType,
	disabled,
}: {
	children: React.ReactNode;
	tooltipType?: TooltipType;
	disabled?: boolean; // TODO: cleanup so don't have random props being passed in
}) {
	return renderWrapper(children, styles.description, tooltipType, disabled);
}

type ConfirmationRowProps = {
	title?: React.ReactNode;
	description?: React.ReactNode;
	/**@default undefined Which tooltip to use on overflow. Overflow -> OverflowText (light), Ellipsis -> EllipsisTooltip (has scrollbars but clones in dom) */
	tooltipType?: TooltipType;
	titleTooltipType?: TooltipType;
	descriptionTooltipType?: TooltipType;
};

export function ConfirmationRow({
	description,
	title,
	tooltipType,
	titleTooltipType,
	descriptionTooltipType,
}: ConfirmationRowProps) {
	const titleTooltip = titleTooltipType ?? tooltipType;
	const descriptionTooltip = descriptionTooltipType ?? tooltipType;
	return (
		<div className={styles.descriptionRow}>
			{typeof title === "string" ? (
				<ConfirmationRowTitle tooltipType={titleTooltip}>{title}</ConfirmationRowTitle>
			) : (
				title
			)}
			{typeof description === "string" ? (
				<ConfirmationRowDescription tooltipType={descriptionTooltip}>{description}</ConfirmationRowDescription>
			) : (
				description
			)}
		</div>
	);
}

type ConfirmationEditButtonProps = {
	onEdit: () => void;
	targetPageName?: EditQuizStep;
	ariaLabel?: string;
	variant?: "text" | "link" | "icon";
	buttonText?: string;
};

export function ConfirmationEditButton({
	targetPageName,
	onEdit,
	variant = "text",
	buttonText = i18nPackage.t("editQuizPage.confirmationBlockTSX.editButtonLabel"),
	ariaLabel,
}: ConfirmationEditButtonProps) {
	const commonProps = {
		onClick: onEdit,
		"aria-label":
			ariaLabel ??
			(targetPageName
				? i18nPackage.t("editQuizPage.confirmationBlockTSX.editButtonAriaLabel", {
						page: getEditQuizStepName(targetPageName),
					})
				: undefined),
	};

	if (variant === "text")
		return (
			<Button {...commonProps} className={styles.editButton} startIcon={<EditIcon />}>
				{buttonText}
			</Button>
		);

	if (variant === "link") {
		return (
			<Link {...commonProps} component="button" type="button" className={styles.editLink}>
				{buttonText}
			</Link>
		);
	}

	return (
		<CoreIconButton
			{...commonProps}
			variant="plain"
			className={styles.editButtonIcon}
			tooltip={
				targetPageName
					? i18nPackage.t("editQuizPage.confirmationBlockTSX.editButtonTooltip", {
							page: getEditQuizStepName(targetPageName),
						})
					: buttonText
			}
		>
			<EditIcon aria-hidden="true" />
		</CoreIconButton>
	);
}

export function ConfirmationBlockSkeleton() {
	return <ConfirmationBlock loading={true} />;
}

export type CommonConfirmationBlockProps = Pick<
	ConfirmationBlockProps,
	"onEdit" | "titleVariant" | "showCheckmark" | "loading"
>;

export type RadioVariantProp = {
	[K in keyof Pick<ConfirmationBlockProps, "variant">]-?: Extract<ConfirmationBlockProps[K], "radio">;
};
export type CompactVariantProp = {
	[K in keyof Pick<ConfirmationBlockProps, "variant">]: Extract<ConfirmationBlockProps[K], "compact">;
};

export type ConfirmationBlockVariant = "radio" | "compact";

type ConfirmationBlockProps = {
	title?: React.ReactNode;
	children?: React.ReactNode;
	onEdit?: () => void;
	icon?: React.ReactNode;
	/**@default "h2" */
	titleVariant?: TypographyVariant;
	error?: boolean;
	showCheckmark?: boolean;
	loading?: boolean;
	/**@default "compact"
	 * radio -> block UI that has the radio icon
	 * compact -> just the title and edit button in a row
	 * Keeping radio variant in case we want to go back
	 */
	variant?: ConfirmationBlockVariant;
	buttonVariant?: ConfirmationEditButtonProps["variant"];
	/**InfoIcon */
	hint?: React.ReactNode;
} & Pick<ConfirmationEditButtonProps, "buttonText" | "targetPageName" | "ariaLabel">;

const ConfirmationBlock = forwardRef<HTMLDivElement, ConfirmationBlockProps>(function ConfirmationBlock(
	{
		children,
		targetPageName,
		title,
		onEdit,
		icon,
		titleVariant = "h2",
		error,
		showCheckmark = true,
		loading,
		variant = "compact",
		buttonVariant = variant === "compact" ? "icon" : "text",
		buttonText,
		ariaLabel,
		hint,
	},
	ref,
) {
	const isCompact = variant === "compact";
	return (
		<CoreSkeletonWrapper loading={loading} variant="rounded" height={150}>
			<div ref={ref} className={`${styles.confirmationContainer} ${isCompact ? styles.compact : ""}`}>
				<div className={styles.headerRow}>
					<div className={styles.headerIconWrapper}>
						{icon && !isCompact && <CoreRadioCardIcon icon={icon} enabled showCheckmark={showCheckmark} />}
						{title && (
							<Typography className={styles.title} variant={titleVariant}>
								{title}
							</Typography>
						)}
						{hint && <CoreInfoIndicator hint={hint} size={16} />}
						{error && (
							<ConditionalWrapper
								condition={isCompact}
								whenTrue={(children) => (
									<Tooltip title={i18nPackage.t("editQuizPage.confirmationBlockTSX.incompleteLabel")}>
										<span>{children}</span>
									</Tooltip>
								)}
								whenFalse={(children) => (
									<Typography component={"span"} className={styles.liveSessionWarning}>
										{children}
										<Typography component={"span"}>
											{i18nPackage.t("editQuizPage.confirmationBlockTSX.incompleteLabel")}
										</Typography>
									</Typography>
								)}
							>
								<IncompleteIcon />
							</ConditionalWrapper>
						)}
					</div>
					{onEdit && (targetPageName || ariaLabel) && (
						<ConfirmationEditButton
							targetPageName={targetPageName}
							onEdit={onEdit}
							variant={buttonVariant}
							buttonText={buttonText}
							ariaLabel={ariaLabel}
						/>
					)}
				</div>
				{children}
			</div>
		</CoreSkeletonWrapper>
	);
});

export default ConfirmationBlock;
